import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { Translate } from "@/app/application/Translate";
import {
	getCardBorderStyle,
	getSurfaceBorderWidth,
} from "@/app/application/theme";
import { useShiftPage } from "@/features/shift/hooks";
import {
	ShiftEventResponse,
	ShiftEventsPagedResponse,
} from "@/features/shift/model/Shift/Shift";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import EventNoteIcon from "@mui/icons-material/EventNote";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
	Box,
	Button,
	Card,
	CardActionArea,
	CardContent,
	Chip,
	Skeleton,
	Stack,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
	alpha,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { SparkLineChart } from "@mui/x-charts/SparkLineChart";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { QuickRangeToggle } from "@/components/date-range/QuickRangeToggle";

import { useFetchShift } from "@/service/ShiftHomeQueries";

dayjs.extend(utc);

const PAGE_PADDING = { xs: 2, sm: 2.5 };
const BOARD_MAX_WIDTH = 1400;

type PeriodKey = "week" | "2weeks" | "month" | "quarter";
type HeatMode = "all" | "handover" | "oos";

const PERIODS: {
	key: PeriodKey;
	days: number;
	chartBuckets: number;
	chartUnit: "day" | "week" | "month";
	labelKey: string;
}[] = [
	{
		key: "week",
		days: 7,
		chartBuckets: 7,
		chartUnit: "day",
		labelKey: "Shift.Dashboard.Period.Week",
	},
	{
		key: "2weeks",
		days: 14,
		chartBuckets: 14,
		chartUnit: "day",
		labelKey: "Shift.Dashboard.Period.2Weeks",
	},
	{
		key: "month",
		days: 30,
		chartBuckets: 4,
		chartUnit: "week",
		labelKey: "Shift.Dashboard.Period.Month",
	},
	{
		key: "quarter",
		days: 90,
		chartBuckets: 3,
		chartUnit: "month",
		labelKey: "Shift.Dashboard.Period.Quarter",
	},
];

function getPeriodConfig(key: string) {
	return PERIODS.find((p) => p.key === key) ?? PERIODS[1];
}

function getEventDate(e: ShiftEventResponse): dayjs.Dayjs | null {
	const d = e.Date ?? e.date ?? e.Created ?? e.created;
	if (!d) return null;
	return dayjs(d);
}

function getEventGroups(e: ShiftEventResponse) {
	return e.Groups ?? e.groups ?? [];
}

function getEventSystems(e: ShiftEventResponse) {
	return e.Systems ?? e.systems ?? [];
}

function toNumber(v: unknown): number {
	const n = typeof v === "string" ? Number(v) : typeof v === "number" ? v : 0;
	return Number.isFinite(n) ? n : 0;
}

function getPagedTotal(data: ShiftEventsPagedResponse | undefined): number {
	return toNumber(data?.Total ?? data?.total);
}

function pctDelta(curr: number, prev: number): number | null {
	if (prev <= 0) return null;
	return ((curr - prev) / prev) * 100;
}

function fmtDelta(pct: number | null): string {
	if (pct === null) return "—";
	const sign = pct > 0 ? "+" : "";
	return `${sign}${pct.toFixed(0)}%`;
}

const panelSx = (t: Theme) => ({
	borderRadius: { xs: 2, sm: 3 },
	border: getCardBorderStyle(t),
	backgroundColor: t.palette.background.paper,
	overflow: "hidden",
	paddingBottom: 2,
	boxShadow:
		t.palette.mode === "dark"
			? "0 1px 0 0 rgba(255,255,255,0.04)"
			: "0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
});

const iconPillSx = (t: Theme, color: string) => ({
	width: 42,
	height: 42,
	borderRadius: 2.5,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	bgcolor: alpha(color, t.palette.mode === "dark" ? 0.18 : 0.12),
	color,
	flexShrink: 0,
});

const subtleSurfaceSx = (t: Theme, color: string) => ({
	bgcolor: alpha(color, t.palette.mode === "dark" ? 0.14 : 0.08),
	color,
});

const secondarySurfaceSx = (t: Theme) => ({
	bgcolor:
		t.palette.mode === "dark"
			? alpha(t.palette.common.white, 0.04)
			: alpha(t.palette.common.white, 0.58),
	border: getCardBorderStyle(t),
});

const elevatedInsetSx = (t: Theme) => ({
	bgcolor:
		t.palette.mode === "dark"
			? alpha(t.palette.common.white, 0.03)
			: alpha(t.palette.common.white, 0.8),
	border: `${getSurfaceBorderWidth(t)} solid ${alpha(
		t.palette.divider,
		t.palette.mode === "dark" ? 0.3 : 0.12,
	)}`,
});

const scrollSx = (t: Theme) => ({
	"&::-webkit-scrollbar": { width: 6 },
	"&::-webkit-scrollbar-track": { background: "transparent" },
	"&::-webkit-scrollbar-thumb": {
		borderRadius: 999,
		background:
			t.palette.mode === "dark"
				? "rgba(255,255,255,0.18)"
				: "rgba(15,23,42,0.16)",
	},
});

const statTileSx = (t: Theme) => ({
	borderRadius: 2,
	border: `${getSurfaceBorderWidth(t)} solid ${alpha(t.palette.divider, 0.75)}`,
	backgroundColor:
		t.palette.mode === "dark"
			? alpha(t.palette.common.white, 0.015)
			: alpha(t.palette.primary.main, 0.02),
	px: 1.25,
	py: 1.25,
});

const readableStatusColor = (t: Theme, tone: "success" | "warning") =>
	t.palette.mode === "dark" ? t.palette[tone].light : t.palette[tone].dark;

type MetricCardProps = {
	title: React.ReactNode;
	subtitle?: React.ReactNode;
	value: number | string;
	icon: React.ReactNode;
	accent: string;
	loading?: boolean;
	onClick?: () => void;
	deltaPct?: number | null;
	deltaLabel?: React.ReactNode;
	sparkline?: number[];
};

const MetricCard = ({
	title,
	subtitle,
	value,
	icon,
	accent,
	loading,
	onClick,
	deltaPct,
	deltaLabel,
}: MetricCardProps) => {
	const theme = useTheme();
	const trendDelta = deltaPct ?? null;

	const readableColor =
		trendDelta === null
			? undefined
			: trendDelta >= 0
				? readableStatusColor(theme, "success")
				: readableStatusColor(theme, "warning");

	return (
		<Card
			variant="outlined"
			sx={{
				...panelSx(theme),
				borderColor: alpha(accent, 0.18),
				height: "100%",
			}}
		>
			<CardContent
				sx={{
					p: { xs: 1.75, sm: 2 },
					height: "100%",
					display: "flex",
					flexDirection: "column",
					"&:last-child": { pb: { xs: 1.75, sm: 2 } },
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "flex-start",
						justifyContent: "space-between",
						gap: 1.5,
					}}
				>
					<Box sx={{ minWidth: 0 }}>
						<Typography variant="body2" color="text.secondary">
							{title}
						</Typography>
						{loading ? (
							<Skeleton variant="text" width="48%" height={40} sx={{ mt: 0.5 }} />
						) : (
							<Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, lineHeight: 1 }}>
								{value}
							</Typography>
						)}
						{subtitle ? (
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ display: "block", mt: 0.5 }}
							>
								{subtitle}
							</Typography>
						) : null}
					</Box>
					<Box sx={iconPillSx(theme, accent)}>{icon}</Box>
				</Box>

				{trendDelta !== null && (
					<Chip
						size="small"
						label={
							<Stack direction="row" spacing={0.5} alignItems="center">
								{deltaLabel && (
									<Typography variant="caption" color="text.secondary">{deltaLabel}</Typography>
								)}
								<Typography variant="caption" fontWeight={900}>{fmtDelta(trendDelta)}</Typography>
							</Stack>
						}
						variant="filled"
						sx={{
							mt: 1.25,
							height: 24,
							borderRadius: 999,
							bgcolor: alpha(
								readableColor ?? theme.palette.text.primary,
								theme.palette.mode === "dark" ? 0.18 : 0.1,
							),
							color: readableColor ?? "text.secondary",
							"& .MuiChip-label": { px: 1 },
						}}
					/>
				)}

				{onClick && (
					<Button
						size="small"
						endIcon={<ArrowForwardIcon fontSize="small" />}
						onClick={onClick}
						sx={{ mt: "auto", pt: 1.5, px: 0, textTransform: "none" }}
					>
						{title}
					</Button>
				)}
			</CardContent>
		</Card>
	);
};

const InsightCard = ({ title, value, subtitle, loading }: InsightCardProps) => {
	const theme = useTheme();
	return (
		<Card
			variant="outlined"
			sx={{
				...panelSx(theme),
				height: "100%",
				borderRadius: { xs: 2, sm: 3 },
				borderColor: alpha(theme.palette.primary.main, 0.1),
			}}
		>
			<CardContent
				sx={{
					p: { xs: 1.75, sm: 2 },
					"&:last-child": { pb: { xs: 1.75, sm: 2 } },
				}}
			>
				<Typography variant="body2" color="text.secondary">
					{title}
				</Typography>
				{loading ? (
					<Skeleton variant="text" width="60%" height={36} sx={{ mt: 0.5 }} />
				) : (
					<Typography
						variant="h6"
						fontWeight={800}
						sx={{ mt: 0.75, lineHeight: 1.15 }}
					>
						{value}
					</Typography>
				)}
				{subtitle ? (
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ display: "block", mt: 0.5 }}
					>
						{subtitle}
					</Typography>
				) : null}
			</CardContent>
		</Card>
	);
};

// ---------- Heatmap helpers/components ----------

type Bucket = {
	key: string;
	label: string;
	startUtc: string;
	endUtc: string;
};

function buildBuckets(cfg: {
	chartUnit: "day" | "week" | "month";
	chartBuckets: number;
}): Bucket[] {
	const out: Bucket[] = [];
	if (cfg.chartUnit === "day") {
		for (let i = 0; i < cfg.chartBuckets; i += 1) {
			const d = dayjs()
				.subtract(cfg.chartBuckets - 1 - i, "day")
				.startOf("day");
			out.push({
				key: d.format("YYYY-MM-DD"),
				label: d.format("ddd"),
				startUtc: d.utc().format(),
				endUtc: d.add(1, "day").subtract(1, "millisecond").utc().format(),
			});
		}
	} else if (cfg.chartUnit === "week") {
		for (let i = 0; i < cfg.chartBuckets; i += 1) {
			const w = dayjs()
				.subtract(cfg.chartBuckets - 1 - i, "week")
				.startOf("week");
			out.push({
				key: w.format("YYYY-MM-DD"),
				label: w.format("DD/MM"),
				startUtc: w.utc().format(),
				endUtc: w.add(1, "week").subtract(1, "millisecond").utc().format(),
			});
		}
	} else {
		for (let i = 0; i < cfg.chartBuckets; i += 1) {
			const m = dayjs()
				.subtract(cfg.chartBuckets - 1 - i, "month")
				.startOf("month");
			out.push({
				key: m.format("YYYY-MM-DD"),
				label: m.format("MMM"),
				startUtc: m.utc().format(),
				endUtc: m.add(1, "month").subtract(1, "millisecond").utc().format(),
			});
		}
	}
	return out;
}

function bucketKeyFor(d: dayjs.Dayjs, unit: "day" | "week" | "month") {
	if (unit === "day") return d.startOf("day").format("YYYY-MM-DD");
	if (unit === "week") return d.startOf("week").format("YYYY-MM-DD");
	return d.startOf("month").format("YYYY-MM-DD");
}

function clamp01(x: number) {
	return Math.max(0, Math.min(1, x));
}

function HeatmapCard({
	title,
	subtitle,
	buckets,
	rows,
	maxLabel,
	onCellClick,
}: {
	title: React.ReactNode;
	subtitle?: React.ReactNode;
	buckets: Bucket[];
	rows: { id: number; name: string; values: number[] }[];
	maxLabel?: string;
	onCellClick?: (args: {
		rowId: number;
		startUtc: string;
		endUtc: string;
	}) => void;
}) {
	const theme = useTheme();
	const isMobile = useMediaQuery("(max-width:767px)");

	const maxVal = useMemo(() => {
		let m = 0;
		for (const r of rows) for (const v of r.values) m = Math.max(m, v);
		return m;
	}, [rows]);

	// Keep it compact and readable
	const leftCol = isMobile ? 112 : 180;

	return (
		<Box
			sx={{
				borderRadius: { xs: 2, sm: 3 },
				...secondarySurfaceSx(theme),
				p: { xs: 1.25, sm: 1.5 },
			}}
		>
			<Stack
				direction="row"
				alignItems="flex-start"
				justifyContent="space-between"
				spacing={1}
				sx={{ mb: 1 }}
			>
				<Box sx={{ minWidth: 0 }}>
					<Typography
						variant="subtitle1"
						fontWeight={700}
						sx={{ lineHeight: 1.1 }}
					>
						{title}
					</Typography>
					{subtitle ? (
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ display: "block", mt: 0.25 }}
						>
							{subtitle}
						</Typography>
					) : null}
				</Box>
				<Chip
					size="small"
					label={`${maxLabel ?? "Max"}: ${maxVal}`}
					sx={{
						borderRadius: 999,
						height: 26,
						...subtleSurfaceSx(theme, theme.palette.primary.main),
					}}
				/>
			</Stack>

			<Box
				sx={{
					overflowX: "auto",
					overflowY: "hidden",
				}}
			>
				<Box
					sx={{
						minWidth: Math.max(
							360,
							leftCol + buckets.length * (isMobile ? 36 : 42),
						),
					}}
				>
					{/* column labels */}
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: `${leftCol}px repeat(${buckets.length}, 1fr)`,
							gap: 0.75,
						}}
					>
						<Box />
						{buckets.map((b) => (
							<Typography
								key={b.key}
								variant="caption"
								color="text.secondary"
								sx={{ textAlign: "center" }}
							>
								{b.label}
							</Typography>
						))}
					</Box>

					{/* grid */}
					<Box
						sx={{
							mt: 0.75,
							display: "grid",
							gridTemplateColumns: `${leftCol}px repeat(${buckets.length}, 1fr)`,
							gap: 0.75,
							alignItems: "center",
						}}
					>
						{rows.map((r) => (
							<React.Fragment key={r.id}>
								<Typography
									variant="body2"
									sx={{
										fontWeight: 700,
										color: "text.secondary",
										pr: 1,
										whiteSpace: "nowrap",
										overflow: "hidden",
										textOverflow: "ellipsis",
									}}
									title={r.name}
								>
									{r.name}
								</Typography>

								{r.values.map((v, i) => {
									const intensity = maxVal === 0 ? 0 : clamp01(v / maxVal);
									const bg = alpha(
										theme.palette.primary.main,
										0.06 + intensity * 0.55,
									);

									return (
										<Tooltip
											key={`${r.id}-${buckets[i].key}`}
											arrow
											title={
												<Box>
													<Typography variant="caption" fontWeight={900}>
														{r.name}
													</Typography>
													<Typography
														variant="caption"
														sx={{ display: "block" }}
													>
														{buckets[i].label}: {v} events
													</Typography>
												</Box>
											}
										>
											<Box
												onClick={() =>
													onCellClick?.({
														rowId: r.id,
														startUtc: buckets[i].startUtc,
														endUtc: buckets[i].endUtc,
													})
												}
												sx={{
													height: 28,
													borderRadius: 1.5,
													bgcolor: bg,
													cursor: onCellClick ? "pointer" : "default",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													fontWeight: 800,
													userSelect: "none",
													transition:
														"transform 120ms ease, box-shadow 120ms ease",
													"&:hover": onCellClick
														? {
																transform: "translateY(-1px)",
																boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.18)}`,
															}
														: undefined,
												}}
											>
												<Typography
													variant="caption"
													sx={{
														fontWeight: 900,
														color:
															intensity > 0.55
																? "primary.contrastText"
																: "text.primary",
													}}
												>
													{v > 0 ? v : ""}
												</Typography>
											</Box>
										</Tooltip>
									);
								})}
							</React.Fragment>
						))}
					</Box>
				</Box>
			</Box>
		</Box>
	);
}

function DayHourHeatmap({
	items,
	onCellClick,
}: {
	items: ShiftEventResponse[];
	onCellClick?: (args: { startUtc: string; endUtc: string }) => void;
}) {
	const theme = useTheme();

	// 7x24 grid
	const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
	const buckets = Array.from({ length: 24 }, (_, h) => ({
		key: String(h),
		label: `${h}`,
	}));

	const matrix = useMemo(() => {
		const grid = Array.from({ length: 7 }, () =>
			Array.from({ length: 24 }, () => 0),
		);
		for (const e of items) {
			const d = getEventDate(e);
			if (!d) continue;
			const dow = (d.day() + 6) % 7; // convert Sunday(0) -> 6, Monday(1)->0
			const hour = d.hour();
			grid[dow][hour] += 1;
		}
		return grid;
	}, [items]);

	const maxVal = useMemo(() => {
		let m = 0;
		for (const row of matrix) for (const v of row) m = Math.max(m, v);
		return m;
	}, [matrix]);

	return (
		<Box
			sx={{
				borderRadius: { xs: 2, sm: 3 },
				...secondarySurfaceSx(theme),
				p: { xs: 1.5, sm: 1.75 },
			}}
		>
			<Stack
				direction="row"
				alignItems="flex-start"
				justifyContent="space-between"
				spacing={1}
				sx={{ mb: 1 }}
			>
				<Box>
					<Box
						sx={{
							display: "inline-flex",
							alignItems: "center",
							gap: 0.75,
							px: 1,
							py: 0.5,
							borderRadius: 999,
							...subtleSurfaceSx(theme, theme.palette.primary.main),
							mb: 0.75,
						}}
					>
						<Typography variant="caption" fontWeight={900}>
							<Translate String="Auto.Features.Shift.Pages.ShiftDashboard.L789C54.PatternHeatmap">Pattern Heatmap</Translate>
						</Typography>
					</Box>
					<Typography
						variant="subtitle1"
						fontWeight={700}
						sx={{ lineHeight: 1.1 }}
					>
						<Translate String="Shift.Dashboard.Patterns">
							Patterns by day & hour
						</Translate>
					</Typography>
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ display: "block", mt: 0.25 }}
					>
						<Translate String="Shift.Dashboard.Patterns.Subtitle">
							Events distribution across weekdays and hours
						</Translate>
					</Typography>
				</Box>
				<Chip
					size="small"
					label={`Max: ${maxVal}`}
					sx={{
						borderRadius: 999,
						height: 28,
						...subtleSurfaceSx(theme, theme.palette.primary.main),
					}}
				/>
			</Stack>

			<Box
				sx={{
					borderRadius: 3,
					...elevatedInsetSx(theme),
					p: 1.25,
					overflowX: "auto",
				}}
			>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: `72px repeat(24, 1fr)`,
						gap: 0.5,
						minWidth: { xs: 600, sm: 720 },
					}}
				>
					<Box />
					{buckets.map((b) => (
						<Typography
							key={b.key}
							variant="caption"
							color="text.secondary"
							sx={{ textAlign: "center" }}
						>
							{b.label}
						</Typography>
					))}

					{days.map((day, r) => (
						<React.Fragment key={day}>
							<Typography
								variant="body2"
								sx={{ fontWeight: 700, color: "text.secondary" }}
							>
								{day}
							</Typography>
							{matrix[r].map((v, c) => {
								const intensity = maxVal === 0 ? 0 : clamp01(v / maxVal);
								const bg = alpha(
									theme.palette.primary.main,
									0.04 + intensity * 0.55,
								);

								// Click filters by that hour block (local day/hour buckets are approximate; good for exploration)
								return (
									<Tooltip
										key={`${day}-${c}`}
										arrow
										title={
											<Typography variant="caption" fontWeight={900}>
												{day} {String(c).padStart(2, "0")}:00 • {v} events
											</Typography>
										}
									>
										<Box
											onClick={() => {
												if (!onCellClick) return;
												// filters "today" for this hour is not meaningful without a date;
												// so we don't click-filter here by default.
											}}
											sx={{
												height: 22,
												borderRadius: 1.5,
												bgcolor: bg,
												transition: "box-shadow 120ms ease",
												"&:hover": {
													boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.16)}`,
												},
											}}
										/>
									</Tooltip>
								);
							})}
						</React.Fragment>
					))}
				</Box>
			</Box>
		</Box>
	);
}

function RankedListCard({
	title,
	subtitle,
	rows,
	loading,
}: {
	title: React.ReactNode;
	subtitle?: React.ReactNode;
	rows: { label: string; value: number }[];
	loading?: boolean;
}) {
	const theme = useTheme();
	const max = Math.max(1, ...rows.map((r) => r.value));

	return (
		<Box
			sx={{
				borderRadius: { xs: 2, sm: 3 },
				...secondarySurfaceSx(theme),
				p: { xs: 1.5, sm: 1.75 },
			}}
		>
			<Box
				sx={{
					display: "inline-flex",
					alignItems: "center",
					gap: 0.75,
					px: 1,
					py: 0.5,
					borderRadius: 999,
					...subtleSurfaceSx(theme, theme.palette.primary.main),
					mb: 0.75,
				}}
			>
				<Typography variant="caption" fontWeight={900}>
					<Translate String="Auto.Features.Shift.Pages.ShiftDashboard.L938C52.RankedBreakdown">Ranked Breakdown</Translate>
				</Typography>
			</Box>
			<Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.1 }}>
				{title}
			</Typography>
			{subtitle ? (
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ display: "block", mt: 0.25, mb: 1 }}
				>
					{subtitle}
				</Typography>
			) : (
				<Box sx={{ mt: 1 }} />
			)}

			{loading ? (
				<Skeleton variant="rounded" height={120} />
			) : rows.length === 0 ? (
				<Typography variant="body2" color="text.secondary">
					—
				</Typography>
			) : (
				<Stack
					spacing={1}
					sx={{
						mt: 0.5,
						borderRadius: 3,
						...elevatedInsetSx(theme),
						p: 1.25,
					}}
				>
					{rows.slice(0, 8).map((r) => (
						<Box
							key={r.label}
							sx={{
								display: "grid",
								gridTemplateColumns: {
									xs: "minmax(0, 1fr) auto",
									sm: "minmax(0, 1fr) auto 108px",
								},
								alignItems: "center",
								gap: 1,
								px: 0.5,
								py: 0.5,
								borderRadius: 2,
								bgcolor: alpha(theme.palette.primary.main, 0.035),
								transition: "background-color 120ms ease",
								"&:hover": {
									bgcolor: alpha(theme.palette.primary.main, 0.06),
								},
							}}
						>
							<Typography
								variant="body2"
								sx={{
									fontWeight: 700,
									color: "text.secondary",
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
								title={r.label}
							>
								{r.label}
							</Typography>
							<Typography variant="body2" fontWeight={900}>
								{r.value}
							</Typography>
							<Box
								sx={{
									gridColumn: { xs: "1 / -1", sm: "auto" },
									width: { xs: "100%", sm: 108 },
									height: 10,
									borderRadius: 999,
									bgcolor:
										theme.palette.mode === "dark"
											? "rgba(255,255,255,0.06)"
											: alpha(theme.palette.text.primary, 0.06),
									overflow: "hidden",
								}}
							>
								<Box
									sx={{
										width: `${(r.value / max) * 100}%`,
										height: "100%",
										background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.info.main} 100%)`,
									}}
								/>
							</Box>
						</Box>
					))}
				</Stack>
			)}
		</Box>
	);
}

// ---------- Main dashboard ----------

interface ShiftDashboardProps {
	onNavigateFromOverview?: () => void;
}

export const ShiftDashboard = ({
	onNavigateFromOverview,
}: ShiftDashboardProps = {}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery("(max-width:767px)");
	const [searchParams, setSearchParams] = useSearchParams();
	const { selectedGroup, groupIdOrUndefined } = useShiftPage();

	const periodKey = (searchParams.get("period") as PeriodKey) || "2weeks";
	const period = getPeriodConfig(periodKey);

	const heatMode = (searchParams.get("heat") as HeatMode) || "all";

	const goToTab = (tab: string) => () => (
		onNavigateFromOverview?.(),
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.set("tab", tab);
				return next;
			},
			{ replace: true },
		)
	);

	const handlePeriodChange = (value: PeriodKey | null) => {
		if (!value) return;
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.set("period", value);
				return next;
			},
			{ replace: true },
		);
	};

	const handleHeatMode = (
		_: React.MouseEvent<HTMLElement>,
		value: HeatMode | null,
	) => {
		if (!value) return;
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.set("heat", value);
				return next;
			},
			{ replace: true },
		);
	};

	// Current period
	const endOfToday = dayjs().endOf("day").utc().format();
	const startOfPeriod = dayjs()
		.subtract(period.days, "day")
		.startOf("day")
		.utc()
		.format();

	// Previous period (same length immediately before)
	const prevEnd = dayjs(startOfPeriod)
		.subtract(1, "millisecond")
		.utc()
		.format();
	const prevStart = dayjs(startOfPeriod)
		.subtract(period.days, "day")
		.startOf("day")
		.utc()
		.format();

	const {
		data: allEvents,
		isLoading: allLoading,
		isError: allError,
	} = useFetchShift({
		GroupId: groupIdOrUndefined,
		StartFromUtc: startOfPeriod,
		EndAtUtc: endOfToday,
		PageSize: 500,
	});

	const { data: allTotalData, isLoading: allTotalLoading } = useFetchShift({
		GroupId: groupIdOrUndefined,
		StartFromUtc: startOfPeriod,
		EndAtUtc: endOfToday,
		PageSize: 1,
	});

	const { data: prevAllTotalData, isLoading: prevAllTotalLoading } =
		useFetchShift({
			GroupId: groupIdOrUndefined,
			StartFromUtc: prevStart,
			EndAtUtc: prevEnd,
			PageSize: 1,
		});

	const { data: handoverTotalData, isLoading: handoverLoading } = useFetchShift(
		{
			GroupId: groupIdOrUndefined,
			IsHandoverEvent: true,
			StartFromUtc: startOfPeriod,
			EndAtUtc: endOfToday,
			PageSize: 1,
		},
	);

	const { data: prevHandoverTotalData, isLoading: prevHandoverLoading } =
		useFetchShift({
			GroupId: groupIdOrUndefined,
			IsHandoverEvent: true,
			StartFromUtc: prevStart,
			EndAtUtc: prevEnd,
			PageSize: 1,
		});

	const { data: oosTotalData, isLoading: oosLoading } = useFetchShift({
		GroupId: groupIdOrUndefined,
		IsOutOfServiceEvent: true,
		StartFromUtc: startOfPeriod,
		EndAtUtc: endOfToday,
		PageSize: 1,
	});

	const { data: prevOosTotalData, isLoading: prevOosLoading } = useFetchShift({
		GroupId: groupIdOrUndefined,
		IsOutOfServiceEvent: true,
		StartFromUtc: prevStart,
		EndAtUtc: prevEnd,
		PageSize: 1,
	});

	const isLoading =
		allLoading ||
		allTotalLoading ||
		prevAllTotalLoading ||
		handoverLoading ||
		prevHandoverLoading ||
		oosLoading ||
		prevOosLoading;

	const periodSubtitle = useMemo(() => {
		if (periodKey === "week")
			return <Translate String="Shift.Dashboard.Period.Week">1 week</Translate>;
		if (periodKey === "2weeks")
			return (
				<Translate String="Shift.Dashboard.Period.2Weeks">2 weeks</Translate>
			);
		if (periodKey === "month")
			return <Translate String="Shift.Dashboard.Period.Month">Month</Translate>;
		if (periodKey === "quarter")
			return (
				<Translate String="Shift.Dashboard.Period.Quarter">Quarter</Translate>
			);
		return (
			<Translate String="Shift.Dashboard.Period.2Weeks">2 weeks</Translate>
		);
	}, [periodKey]);

	const items = useMemo(() => {
		const arr = allEvents?.Items ?? allEvents?.items;
		return Array.isArray(arr) ? arr : [];
	}, [allEvents]);

	// Time series for sparklines + some insights (keep your existing approach)
	const timeSeries = useMemo(() => {
		const cfg = period;
		const buckets: {
			key: string;
			label: string;
			total: number;
			handover: number;
			oos: number;
		}[] = [];
		const keyToIndex = new Map<string, number>();

		const add = (label: string, start: dayjs.Dayjs) => {
			const key = start.format("YYYY-MM-DDTHH:mm:ss");
			keyToIndex.set(key, buckets.length);
			buckets.push({ key, label, total: 0, handover: 0, oos: 0 });
		};

		if (cfg.chartUnit === "day") {
			for (let i = 0; i < cfg.chartBuckets; i += 1) {
				const d = dayjs()
					.subtract(cfg.chartBuckets - 1 - i, "day")
					.startOf("day");
				add(d.format("ddd"), d);
			}
		} else if (cfg.chartUnit === "week") {
			for (let i = 0; i < cfg.chartBuckets; i += 1) {
				const w = dayjs()
					.subtract(cfg.chartBuckets - 1 - i, "week")
					.startOf("week");
				add(w.format("DD/MM"), w);
			}
		} else {
			for (let i = 0; i < cfg.chartBuckets; i += 1) {
				const m = dayjs()
					.subtract(cfg.chartBuckets - 1 - i, "month")
					.startOf("month");
				add(m.format("MMM"), m);
			}
		}

		const idxFor = (d: dayjs.Dayjs) => {
			const k =
				cfg.chartUnit === "day"
					? d.startOf("day").format("YYYY-MM-DDTHH:mm:ss")
					: cfg.chartUnit === "week"
						? d.startOf("week").format("YYYY-MM-DDTHH:mm:ss")
						: d.startOf("month").format("YYYY-MM-DDTHH:mm:ss");
			return keyToIndex.has(k) ? (keyToIndex.get(k) as number) : null;
		};

		for (const e of items) {
			const d = getEventDate(e);
			if (!d) continue;
			const idx = idxFor(d);
			if (idx === null) continue;

			const isHandover = toNumber(e.IsHandoverEvent ?? e.isHandoverEvent) === 1;
			const isOos =
				toNumber(
					e.IsOutOfService ??
						e.isOutOfService ??
						e.IsOutOfServiceEvent ??
						e.isOutOfServiceEvent,
				) === 1;

			buckets[idx].total += 1;
			if (isHandover) buckets[idx].handover += 1;
			if (isOos) buckets[idx].oos += 1;
		}

		return buckets;
	}, [items, period]);

	const metrics = useMemo(() => {
		const totalInPeriod = getPagedTotal(allTotalData);
		const prevTotal = getPagedTotal(prevAllTotalData);

		const handoverCount = getPagedTotal(handoverTotalData);
		const prevHandover = getPagedTotal(prevHandoverTotalData);

		const oosCount = getPagedTotal(oosTotalData);
		const prevOos = getPagedTotal(prevOosTotalData);

		const today = dayjs().format("YYYY-MM-DD");
		const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD");

		const todayCount = items.filter(
			(e) => getEventDate(e)?.format("YYYY-MM-DD") === today,
		).length;
		const yesterdayCount = items.filter(
			(e) => getEventDate(e)?.format("YYYY-MM-DD") === yesterday,
		).length;

		return {
			todayCount,
			yesterdayCount,
			totalInPeriod,
			prevTotal,
			handoverCount,
			prevHandover,
			oosCount,
			prevOos,
		};
	}, [
		allTotalData,
		prevAllTotalData,
		handoverTotalData,
		prevHandoverTotalData,
		oosTotalData,
		prevOosTotalData,
		items,
	]);

	const deltas = useMemo(
		() => ({
			today: pctDelta(metrics.todayCount, metrics.yesterdayCount),
			total: pctDelta(metrics.totalInPeriod, metrics.prevTotal),
			handover: pctDelta(metrics.handoverCount, metrics.prevHandover),
			oos: pctDelta(metrics.oosCount, metrics.prevOos),
		}),
		[metrics],
	);

	const sparklines = useMemo(
		() => ({
			total: timeSeries.map((b) => b.total),
			handover: timeSeries.map((b) => b.handover),
			oos: timeSeries.map((b) => b.oos),
		}),
		[timeSeries],
	);

	const insights = useMemo(() => {
		const activeDays = new Set<string>();
		let busiest: { label: string; count: number } | null = null;

		const systemCount = new Map<string, number>();
		for (const e of items) {
			const d = getEventDate(e);
			if (d) activeDays.add(d.format("YYYY-MM-DD"));

			const systems = getEventSystems(e);
			for (const s of systems) {
				const name = String(s.Name ?? s.name ?? "").trim();
				if (!name) continue;
				systemCount.set(name, (systemCount.get(name) ?? 0) + 1);
			}
		}

		for (const b of timeSeries) {
			if (!busiest || b.total > busiest.count)
				busiest = { label: b.label, count: b.total };
		}

		let topSystem: string | null = null;
		let topSystemCount = 0;
		for (const [name, c] of systemCount.entries()) {
			if (c > topSystemCount) {
				topSystem = name;
				topSystemCount = c;
			}
		}

		const avgPerActiveDay =
			metrics.totalInPeriod > 0
				? metrics.totalInPeriod / Math.max(activeDays.size, 1)
				: 0;
		const handoverRate =
			metrics.totalInPeriod > 0
				? (metrics.handoverCount / metrics.totalInPeriod) * 100
				: 0;
		const oosRate =
			metrics.totalInPeriod > 0
				? (metrics.oosCount / metrics.totalInPeriod) * 100
				: 0;

		return {
			activeDays: activeDays.size,
			avgPerActiveDay,
			busiest,
			topSystem,
			topSystemCount,
			handoverRate,
			oosRate,
		};
	}, [
		items,
		timeSeries,
		metrics.totalInPeriod,
		metrics.handoverCount,
		metrics.oosCount,
	]);

	const hasData =
		metrics.totalInPeriod > 0 ||
		metrics.handoverCount > 0 ||
		metrics.oosCount > 0;

	const cardAccents = {
		today: theme.palette.primary.main,
		total: theme.palette.info.main,
		handover: theme.palette.success.main,
		oos: theme.palette.warning.main,
	};

	// ------- A) Groups x time bucket heatmap (top N groups) -------
	const groupHeat = useMemo(() => {
		const buckets = buildBuckets(period);
		const bucketIndex = new Map<string, number>();
		buckets.forEach((b, i) => bucketIndex.set(b.key, i));

		const passMode = (e: ShiftEventResponse) => {
			if (heatMode === "handover")
				return toNumber(e.IsHandoverEvent ?? e.isHandoverEvent) === 1;
			if (heatMode === "oos") {
				return (
					toNumber(
						e.IsOutOfService ??
							e.isOutOfService ??
							e.IsOutOfServiceEvent ??
							e.isOutOfServiceEvent,
					) === 1
				);
			}
			return true;
		};

		// Count group totals first
		const totals = new Map<number, { name: string; count: number }>();
		for (const e of items) {
			if (!passMode(e)) continue;
			const groups = getEventGroups(e);
			for (const g of groups) {
				const id = Number(g.Id ?? g.id);
				const name = String(g.Name ?? g.name ?? "").trim();
				if (!Number.isFinite(id) || !name) continue;
				const prev = totals.get(id);
				totals.set(id, { name, count: (prev?.count ?? 0) + 1 });
			}
		}

		const top = [...totals.entries()]
			.sort((a, b) => b[1].count - a[1].count)
			.slice(0, 10) // top N
			.map(([id, meta]) => ({ id, name: meta.name }));

		const valuesByGroup = new Map<number, number[]>();
		for (const g of top)
			valuesByGroup.set(
				g.id,
				Array.from({ length: buckets.length }, () => 0),
			);

		for (const e of items) {
			if (!passMode(e)) continue;

			const d = getEventDate(e);
			if (!d) continue;
			const key = bucketKeyFor(d, period.chartUnit);
			const bi = bucketIndex.get(key);
			if (bi === undefined) continue;

			const groups = getEventGroups(e);
			for (const g of groups) {
				const id = Number(g.Id ?? g.id);
				if (!valuesByGroup.has(id)) continue;
				valuesByGroup.get(id)![bi] += 1;
			}
		}

		return {
			buckets,
			rows: top.map((g) => ({
				id: g.id,
				name: g.name,
				values: valuesByGroup.get(g.id) ?? [],
			})),
		};
	}, [items, period, heatMode]);

	// ------- C) Ranked bars: top groups + top systems -------
	const ranked = useMemo(() => {
		const groups = new Map<string, number>();
		const systems = new Map<string, number>();

		for (const e of items) {
			const gs = getEventGroups(e);
			for (const g of gs) {
				const name = String(g.Name ?? g.name ?? "").trim();
				if (!name) continue;
				groups.set(name, (groups.get(name) ?? 0) + 1);
			}

			const ss = getEventSystems(e);
			for (const s of ss) {
				const name = String(s.Name ?? s.name ?? "").trim();
				if (!name) continue;
				systems.set(name, (systems.get(name) ?? 0) + 1);
			}
		}

		const topGroups = [...groups.entries()]
			.sort((a, b) => b[1] - a[1])
			.map(([label, value]) => ({ label, value }));
		const topSystems = [...systems.entries()]
			.sort((a, b) => b[1] - a[1])
			.map(([label, value]) => ({ label, value }));

		return { topGroups, topSystems };
	}, [items]);

	const uniqueGroupsInPeriod = useMemo(() => {
		const names = new Set<string>();
		for (const e of items) {
			const groups = getEventGroups(e);
			for (const g of groups) {
				const name = String(g.Name ?? g.name ?? "").trim();
				if (name) names.add(name);
			}
		}
		return names.size;
	}, [items]);

	const uniqueSystemsInPeriod = useMemo(() => {
		const names = new Set<string>();
		for (const e of items) {
			const systems = getEventSystems(e);
			for (const s of systems) {
				const name = String(s.Name ?? s.name ?? "").trim();
				if (name) names.add(name);
			}
		}
		return names.size;
	}, [items]);

	// Automatic switch:
	// - All groups => show group heatmap (A)
	// - Single group => show systems distribution + day/hour heatmap (B)
	const showGroupHeat = selectedGroup === "all";

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				flex: 1,
				minHeight: 0,
				overflowY: "auto",
				overflowX: "hidden",
				px: PAGE_PADDING,
				pt: 1.5,
				pb: { xs: 2.5, sm: 3 },
				backgroundColor: "background.default",
				...scrollSx(theme),
				WebkitOverflowScrolling: "touch",
			}}
		>
			<Box
				sx={{
					width: "100%",
					display: "flex",
					flexDirection: "column",
					gap: { xs: 1.5, sm: 2 },
					maxWidth: BOARD_MAX_WIDTH,
					mx: "auto",
				}}
			>
				<Card
					variant="outlined"
					sx={{
						...panelSx(theme),
						borderColor: alpha(theme.palette.primary.main, 0.14),
					}}
				>
					<CardContent
						sx={{
							display: "grid",
							gridTemplateColumns: {
								xs: "1fr",
								lg: "minmax(0, 1.3fr) minmax(340px, 0.7fr)",
							},
							alignItems: "start",
							gap: 2,
							p: { xs: 2, sm: 2.5 },
						}}
					>
						<Box sx={{ minWidth: 0 }}>
							<Stack
								direction="row"
								spacing={0.75}
								alignItems="center"
								sx={{ mb: 0.5 }}
							>
								<AssessmentOutlinedIcon
									sx={{ fontSize: 18, color: "primary.main" }}
								/>
								<Typography
									variant="caption"
									sx={{
										fontWeight: 700,
										color: "primary.main",
										letterSpacing: "0.06em",
										textTransform: "uppercase",
									}}
								>
									<Translate String="Auto.Features.Shift.Pages.ShiftDashboard.L1614C10.ShiftDashboard">Shift dashboard</Translate>
								</Typography>
							</Stack>
							<Typography
								variant="h4"
								component="h1"
								sx={{
									fontWeight: 700,
									letterSpacing: "-0.02em",
									lineHeight: 1.1,
									fontSize: { xs: "1.4rem", sm: "1.8rem" },
								}}
							>
								<Translate String="Auto.Features.Shift.Pages.ShiftDashboard.L1627C9.ShiftDashboard">Shift dashboard</Translate>
							</Typography>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ mt: 0.75 }}
							>
								<Translate String="Auto.Features.Shift.Pages.ShiftDashboard.L1634C9.TrackEventVolumeHandoversAndOut">Track event volume, handovers, and out-of-service activity
								across the selected shift scope.</Translate>
							</Typography>

							<Stack
								direction="row"
								spacing={0.75}
								flexWrap="wrap"
								useFlexGap
								sx={{ mt: 1.5 }}
							>
								<Chip
									size="small"
									label={`${dayjs(startOfPeriod).format("DD/MM/YY")} - ${dayjs(endOfToday).format("DD/MM/YY")}`}
									sx={{
										height: 28,
										borderRadius: 999,
										fontWeight: 800,
										...subtleSurfaceSx(theme, theme.palette.primary.main),
									}}
								/>
								<Chip
									size="small"
									label={
										selectedGroup === "all"
											? "All groups"
											: `Group ${selectedGroup}`
									}
									sx={{
										height: 28,
										borderRadius: 999,
										fontWeight: 800,
										...subtleSurfaceSx(
											theme,
											theme.palette.mode === "dark"
												? theme.palette.info.light
												: theme.palette.info.dark,
										),
									}}
								/>
								<Chip
									size="small"
									label={`${isLoading ? "..." : metrics.totalInPeriod} events`}
									sx={{
										height: 28,
										borderRadius: 999,
										fontWeight: 800,
										...subtleSurfaceSx(
											theme,
											theme.palette.mode === "dark"
												? theme.palette.success.light
												: theme.palette.success.dark,
										),
									}}
								/>
							</Stack>
						</Box>

						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 1,
							}}
						>
							<QuickRangeToggle
								value={periodKey}
								onChange={handlePeriodChange}
								options={[
									{ value: "week", label: "1w" },
									{ value: "2weeks", label: "2w" },
									{ value: "month", label: "1m" },
									{ value: "quarter", label: "3m" },
								]}
								ariaLabel="Shift date range"
								groupProps={{
									sx: {
										width: { xs: "100%", sm: "auto" },
										flexWrap: "wrap",
										gap: 0.75,
										"& .MuiToggleButton-root": {
											textTransform: "none",
											fontWeight: 700,
											px: 1.25,
											borderRadius: "4px !important",
											border: `1px solid ${theme.palette.divider} !important`,
											backgroundColor: theme.palette.background.paper,
											color: "text.secondary",
											minHeight: { xs: 36, sm: "auto" },
											flex: { xs: "1 1 calc(50% - 6px)", sm: "0 0 auto" },
										},
										"& .MuiToggleButton-root.Mui-selected": {
											color: "primary.main",
											backgroundColor: alpha(theme.palette.primary.main, 0.06),
											borderColor: `${theme.palette.primary.main} !important`,
										},
									},
								}}
							/>

							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
									gap: 1,
								}}
							>
								<Box sx={statTileSx(theme)}>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{
											fontWeight: 700,
											textTransform: "uppercase",
											letterSpacing: "0.04em",
										}}
									>
										<Translate String="Auto.Features.Shift.Pages.ShiftDashboard.L1754C11.Today">Today</Translate>
									</Typography>
									<Typography
										variant="subtitle2"
										sx={{ fontWeight: 800, mt: 0.2, lineHeight: 1.2 }}
									>
										{isLoading ? "—" : metrics.todayCount}
									</Typography>
								</Box>
								<Box sx={statTileSx(theme)}>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{
											fontWeight: 700,
											textTransform: "uppercase",
											letterSpacing: "0.04em",
										}}
									>
										<Translate String="Auto.Features.Shift.Pages.ShiftDashboard.L1773C11.InPeriod">In period</Translate>
									</Typography>
									<Typography
										variant="subtitle2"
										sx={{ fontWeight: 800, mt: 0.2, lineHeight: 1.2 }}
									>
										{isLoading ? "—" : metrics.totalInPeriod}
									</Typography>
								</Box>
								<Box sx={statTileSx(theme)}>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{
											fontWeight: 700,
											textTransform: "uppercase",
											letterSpacing: "0.04em",
										}}
									>
										<Translate String="Auto.Features.Shift.Pages.ShiftDashboard.L1792C11.ActiveGroups">Active groups</Translate>
									</Typography>
									<Typography
										variant="subtitle2"
										sx={{ fontWeight: 800, mt: 0.2, lineHeight: 1.2 }}
									>
										{isLoading ? "—" : uniqueGroupsInPeriod}
									</Typography>
								</Box>
								<Box sx={statTileSx(theme)}>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{
											fontWeight: 700,
											textTransform: "uppercase",
											letterSpacing: "0.04em",
										}}
									>
										<Translate String="Auto.Features.Shift.Pages.ShiftDashboard.L1811C11.SystemsTouched">Systems touched</Translate>
									</Typography>
									<Typography
										variant="subtitle2"
										sx={{ fontWeight: 800, mt: 0.2, lineHeight: 1.2 }}
									>
										{isLoading ? "—" : uniqueSystemsInPeriod}
									</Typography>
								</Box>
							</Box>
						</Box>
					</CardContent>
				</Card>

				{allError && (
					<Card
						variant="outlined"
						sx={{
							borderColor: (t) => alpha(t.palette.error.main, 0.3),
							bgcolor: (t) =>
								alpha(
									t.palette.error.main,
									t.palette.mode === "dark" ? 0.12 : 0.06,
								),
							px: 2,
							py: 1.5,
							borderRadius: 2,
						}}
					>
						<Typography variant="body2" fontWeight={700} color="error.main">
							<Translate String="Shift.Dashboard.Error">
								Failed to load shift data. Please try again.
							</Translate>
						</Typography>
					</Card>
				)}

				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: {
							xs: "1fr",
							sm: "repeat(2, minmax(0, 1fr))",
							xl: "repeat(4, minmax(0, 1fr))",
						},
						gap: 2,
					}}
				>
					<MetricCard
						title={<Translate String="Shift.Dashboard.Today">Today</Translate>}
						subtitle={
							<Translate String="Shift.Dashboard.EventsLogged">
								Events logged
							</Translate>
						}
						value={metrics.todayCount}
						icon={<EventNoteIcon fontSize="small" />}
						accent={cardAccents.today}
						loading={isLoading}
						onClick={() => (
							onNavigateFromOverview?.(),
							setSearchParams(
								(prev) => {
									const next = new URLSearchParams(prev);
									next.set("tab", "log");
									return next;
								},
								{ replace: true },
							)
						)}
						deltaPct={deltas.today}
						deltaLabel={
							<Translate String="Shift.Dashboard.VsYesterday">
								vs yesterday
							</Translate>
						}
						sparkline={sparklines.total.slice(-10)}
					/>
					<MetricCard
						title={
							<Translate String="Shift.Dashboard.InPeriod">In period</Translate>
						}
						subtitle={periodSubtitle}
						value={metrics.totalInPeriod}
						icon={<AssessmentOutlinedIcon fontSize="small" />}
						accent={cardAccents.total}
						loading={isLoading}
						onClick={goToTab("log")}
						deltaPct={deltas.total}
						deltaLabel={
							<Translate String="Shift.Dashboard.VsPrevPeriod">
								vs prev
							</Translate>
						}
						sparkline={sparklines.total}
					/>
					<MetricCard
						title={
							<Translate String="Shift.Event.Handover">Handover</Translate>
						}
						subtitle={periodSubtitle}
						value={metrics.handoverCount}
						icon={<SwapHorizIcon fontSize="small" />}
						accent={cardAccents.handover}
						loading={isLoading}
						onClick={goToTab("handover")}
						deltaPct={deltas.handover}
						deltaLabel={
							<Translate String="Shift.Dashboard.VsPrevPeriod">
								vs prev
							</Translate>
						}
						sparkline={sparklines.handover}
					/>
					<MetricCard
						title={
							<Translate String="Shift.Event.OutOfService">
								Out of service
							</Translate>
						}
						subtitle={periodSubtitle}
						value={metrics.oosCount}
						icon={<BuildOutlinedIcon fontSize="small" />}
						accent={cardAccents.oos}
						loading={isLoading}
						onClick={goToTab("outOfService")}
						deltaPct={deltas.oos}
						deltaLabel={
							<Translate String="Shift.Dashboard.VsPrevPeriod">
								vs prev
							</Translate>
						}
						sparkline={sparklines.oos}
					/>
				</Box>

				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: {
							xs: "1fr",
							lg: "repeat(3, minmax(0, 1fr))",
						},
						gap: 2,
					}}
				>
					<InsightCard
						title={
							<Translate String="Shift.Dashboard.Insight.Busiest">
								Busiest bucket
							</Translate>
						}
						value={
							insights.busiest
								? `${insights.busiest.label} • ${insights.busiest.count}`
								: "—"
						}
						subtitle={
							<Translate String="Shift.Dashboard.Insight.BusiestHint">
								Highest activity in the selected period
							</Translate>
						}
						loading={isLoading}
					/>
					<InsightCard
						title={
							<Translate String="Shift.Dashboard.Insight.Activity">
								Activity spread
							</Translate>
						}
						value={`${insights.activeDays} ${insights.activeDays === 1 ? "day" : "days"}`}
						subtitle={
							<Translate String="Shift.Dashboard.Insight.ActivityHint">
								Avg {insights.avgPerActiveDay.toFixed(1)} events per active day
							</Translate>
						}
						loading={isLoading}
					/>
					<InsightCard
						title={
							<Translate String="Shift.Dashboard.Insight.TopSystem">
								Top system
							</Translate>
						}
						value={
							insights.topSystem
								? `${insights.topSystem} • ${insights.topSystemCount}`
								: "—"
						}
						subtitle={
							<Translate String="Shift.Dashboard.Insight.Rates">
								Handover {insights.handoverRate.toFixed(0)}% • OOS{" "}
								{insights.oosRate.toFixed(0)}%
							</Translate>
						}
						loading={isLoading}
					/>
				</Box>

				{/* Replace stacked chart with: A/B/C according to selectedGroup */}
				<Card variant="outlined" sx={panelSx(theme)}>
				<CardContent
					sx={{
						p: { xs: 2, sm: 2.5 },
						"&:last-child": { pb: { xs: 2, sm: 2.5 } },
					}}
				>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={1.5}
						alignItems={{ xs: "flex-start", sm: "center" }}
						justifyContent="space-between"
						sx={{ mb: 2 }}
					>
						<Box sx={{ minWidth: 0 }}>
							<Box
								sx={{
									display: "inline-flex",
									alignItems: "center",
									gap: 1,
									px: 1.25,
									py: 0.75,
									borderRadius: 999,
									...subtleSurfaceSx(theme, theme.palette.primary.main),
									mb: 1,
								}}
							>
								<AssessmentOutlinedIcon sx={{ fontSize: 18 }} />
								<Typography variant="caption" fontWeight={800}>
									<Translate String="Auto.Features.Shift.Pages.ShiftDashboard.L2036C57.ShiftActivityMap">Shift activity map</Translate>
								</Typography>
							</Box>
							<Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.15 }}>
								{showGroupHeat
									? <Translate String="Shift.Dashboard.ActivityMap.TitleGroup">Workload distribution</Translate>
									: <Translate String="Shift.Dashboard.ActivityMap.TitleSingle">Event patterns and rankings</Translate>}
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								{showGroupHeat
									? <Translate String="Shift.Dashboard.ActivityMap.SubGroup">Review event concentration over time with a scannable heatmap by group.</Translate>
									: <Translate String="Shift.Dashboard.ActivityMap.SubSingle">Ranked lists and time patterns to spot the busiest systems and hours.</Translate>}
							</Typography>
						</Box>

						{showGroupHeat ? (
							<ToggleButtonGroup
								exclusive
								value={heatMode}
								onChange={handleHeatMode}
								size="small"
								color="primary"
								sx={{
									width: { xs: "100%", sm: "auto" },
									flexWrap: "wrap",
									gap: 0.75,
									"& .MuiToggleButton-root": {
										textTransform: "none",
										fontWeight: 700,
										fontSize: "0.75rem",
										py: 0.65,
										px: 1.25,
										borderRadius: "4px !important",
										border: `1px solid ${theme.palette.divider} !important`,
										color: "text.secondary",
										minHeight: { xs: 36, sm: "auto" },
										flex: { xs: "1 1 calc(33.333% - 6px)", sm: "0 0 auto" },
									},
									"& .MuiToggleButton-root.Mui-selected": {
										color: "primary.main",
										backgroundColor: alpha(theme.palette.primary.main, 0.06),
										borderColor: `${theme.palette.primary.main} !important`,
									},
								}}
							>
								<ToggleButton value="all">
									<Translate String="Shift.Dashboard.Heat.All">All</Translate>
								</ToggleButton>
								<ToggleButton value="handover">
									<Translate String="Shift.Dashboard.Heat.Handover">Handover</Translate>
								</ToggleButton>
								<ToggleButton value="oos">
									<Translate String="Shift.Dashboard.Heat.OOS">OOS</Translate>
								</ToggleButton>
							</ToggleButtonGroup>
						) : null}
					</Stack>

					{isLoading ? (
						<Skeleton variant="rounded" height={isMobile ? 220 : 260} />
					) : showGroupHeat ? (
							<HeatmapCard
								title={
									<Translate String="Shift.Dashboard.Workload">
										Workload by group
									</Translate>
								}
								subtitle={
									<Translate String="Shift.Dashboard.Workload.Subtitle">
										Compare event concentration over time for the most active
										groups.
									</Translate>
								}
								maxLabel="Max"
								buckets={groupHeat.buckets}
								rows={groupHeat.rows}
								onCellClick={({ rowId, startUtc, endUtc }) => {
									onNavigateFromOverview?.();
									setSearchParams(
										(prev) => {
											const next = new URLSearchParams(prev);
											next.set("tab", "log");
											next.set("StartFromUtc", startUtc);
											next.set("EndAtUtc", endUtc);
											// Adjust this param to match what your log expects:
											next.set("GroupId", String(rowId));
											return next;
										},
										{ replace: true },
									);
								}}
							/>
						) : (
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: {
										xs: "1fr",
										lg: "repeat(2, minmax(0, 1fr))",
									},
									gap: 2,
								}}
							>
								<RankedListCard
									title={
										<Translate String="Shift.Dashboard.TopSystems">
											Top systems
										</Translate>
									}
									subtitle={
										<Translate String="Shift.Dashboard.TopSystems.Subtitle">
											Most frequent systems in period
										</Translate>
									}
									rows={ranked.topSystems}
									loading={isLoading}
								/>
								<DayHourHeatmap items={items} />
								<Box sx={{ gridColumn: { xs: "auto", lg: "1 / -1" } }}>
									<RankedListCard
										title={
											<Translate String="Shift.Dashboard.TopGroups">
												Top groups
											</Translate>
										}
										subtitle={
											<Translate String="Shift.Dashboard.TopGroups.Subtitle">
												Most frequent groups in period
											</Translate>
										}
										rows={ranked.topGroups}
										loading={isLoading}
									/>
								</Box>
							</Box>
						)}
					</CardContent>
				</Card>

				{/* Empty state */}
				{!isLoading && !allError && !hasData && (
					<Box
						sx={{
							mt: 2,
							p: 2.25,
							textAlign: "center",
							borderRadius: 2.25,
							border: `1px dashed ${theme.palette.divider}`,
							bgcolor:
								theme.palette.mode === "dark"
									? "rgba(255,255,255,0.03)"
									: "rgba(15,23,42,0.02)",
						}}
					>
						<Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
							<Translate String="Shift.Dashboard.NoEvents">
								No shift events in the last {period.days} days.
							</Translate>
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
							<Translate String="Shift.Dashboard.NoEventsHint">
								Try a different group/period, or log a new event to see metrics
								here.
							</Translate>
						</Typography>
						<Stack
							direction={{ xs: "column", sm: "row" }}
							spacing={1}
							justifyContent="center"
						>
							<Button variant="contained" onClick={goToTab("log")}>
								<Translate String="Shift.Dashboard.CTA.GoToLog">
									Go to Shift Log
								</Translate>
							</Button>
							<Button
								variant="outlined"
								sx={{ borderRadius: 2 }}
								onClick={() => {
									setSearchParams(
										(prev) => {
											const next = new URLSearchParams(prev);
											next.set("period", "2weeks");
											return next;
										},
										{ replace: true },
									);
								}}
							>
								<Translate String="Shift.Dashboard.CTA.Try2Weeks">
									Try 2 weeks
								</Translate>
							</Button>
						</Stack>
					</Box>
				)}
			</Box>
		</Box>
	);
};
